
import type { Badge } from './types';
import type { PremiumFeatureKey } from './components/PremiumNudge';

export const APP_URL = 'https://app.fricamp.com';

export const MAP_DEFAULT_CENTER = {
  latitude: 61.125,
  longitude: 9.105
};

export const MAP_ZOOM_LEVEL = 14;

export const SCORE_COLORS = {
  HIGH: '#22c55e', // Grønn
  MEDIUM: '#eab308', // Gul
  LOW: '#ef4444', // Rød
  PROTECTED: '#a855f7', // Lilla
};

export const ROUTE_COLOR_PALETTE = [
  '#ef4444', // Red 500
  '#f97316', // Orange 500
  '#f59e0b', // Amber 500
  '#84cc16', // Lime 500
  '#10b981', // Emerald 500
  '#06b6d4', // Cyan 500
  '#3b82f6', // Blue 500
  '#8b5cf6', // Violet 500
  '#d946ef', // Fuchsia 500
  '#f43f5e', // Rose 500
  '#14b8a6', // Teal 500
  '#6366f1', // Indigo 500
];

export const MAP_LAYERS = {
  // OpenTopoMap is extremely reliable for topography
  TOPO: 'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
  DARK: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
  SATELLITE: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
  SATELLITE_LABELS: 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
  NORWAY_TOPO: 'https://cache.kartverket.no/v1/wmts/1.0.0/topo/default/webmercator/{z}/{y}/{x}.png',

  // Oppdatert til den mest stabile WMS-inngangen for turløyper
  GEONORGE_HIKING_WMS: 'https://wms.geonorge.no/skwms1/wms.friluftsruter2?',

  GOOGLE_HYBRID: 'https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}',

  NKOM_COVERAGE_WMS: 'https://wms.geonorge.no/skwms1/wms.nkom_mobildekning',

  // Eiendomsgrenser (Kartverket Matrikkel WMS — offentlig tjeneste)
  PROPERTY_BOUNDARIES_WMS: 'https://wms.geonorge.no/skwms1/wms.matrikkelkart?',

  // Terrengskygge (Hillshade) — Kartverket WMTS
  HILLSHADE: 'https://cache.kartverket.no/v1/wmts/1.0.0/hillshade/default/webmercator/{z}/{y}/{x}.png',

  // Fiskeridirektoratet WMS — Gytefelt og fredningsområder
  FISKERIDIR_GYTEFELT_WMS: 'https://gis.fiskeridir.no/server/services/fiskeridirWMS_fiskeri/MapServer/WMSServer?',
  FISKERIDIR_FREDNING_WMS: 'https://gis.fiskeridir.no/server/services/Yggdrasil/Fiskerireguleringer/MapServer/WMSServer?',

  // Traktorveg og Skogsbilveg (Kartverket WMS)
  FOREST_ROADS_WMS: 'https://wms.geonorge.no/skwms1/wms.traktorveg_skogsbilveger?',

  // Vegvesenet WMS
  SVV_NVDB_WMS: 'https://nvdbwms.atlas.vegvesen.no/v2/nvdbwms',
  SVV_TS_WMS: 'https://tswms.atlas.vegvesen.no/v1/tswms',

  // Norgeskart High-res Topo (Kartverket)
  NORGESKART_TOPO_WMS: 'https://wms.geonorge.no/skwms1/wms.topo4?',
  // Sea Depth / Sjokart (Kartverket)
  SEA_DEPTH_WMS: 'https://wms.geonorge.no/skwms1/wms.sjokart_raster?'
};

// --- Offline map tile caching ---

/**
 * Slår på plattform-uavhengig ambient flis-cache (CachedTileLayer + tileCache).
 * Sett til false for å falle tilbake til vanlige Leaflet-flislag hvis noe
 * skulle oppføre seg feil i produksjon.
 */
export const TILE_CACHE_ENABLED = true;

/** Wifi-prefetch: hent fliser rundt viewport på forhånd (kun umålt nett). */
export const TILE_PREFETCH_ENABLED = true;

/**
 * Ekte bakgrunns-innsjekk via native OS-geofence (Android GeofencingClient m/
 * DWELL + iOS region monitoring). Når på: registrerer 150 m-soner rundt de
 * nærmeste innsjekk-egnede plassene og fyrer «vil du sjekke inn?»-varsel etter
 * PROXIMITY_DWELL_MS opphold — også når appen er lukket. Kun native (web no-op).
 * Se services/geofenceManager.ts. Trinn 1 (forgrunn) virker uavhengig av dette.
 */
export const GEOFENCE_CHECKIN_ENABLED = true;

/** Maks antall geofence-soner å registrere samtidig (iOS hard-tak = 20). */
export const GEOFENCE_MAX_REGIONS = 20;

/** Radius (meter) for innsjekk-geofence rundt en plass. */
export const GEOFENCE_RADIUS_M = 150;

/** localStorage-flagg: bruker har skrudd AV bakgrunns-innsjekk (respekteres av hooken). */
export const GEOFENCE_OPTOUT_KEY = 'fricamp_geofence_optout';

export const OFFLINE_TILE_CACHE_NAME = 'fricamp-tiles-v1';
export const OFFLINE_ZOOM_MIN = 10;
export const OFFLINE_ZOOM_MAX = 16;
export const MAX_OFFLINE_TILES = 15_000;
export const TILE_SIZE_ESTIMATES: Record<'NORWAY_TOPO' | 'SATELLITE', number> = {
  NORWAY_TOPO: 18_000, // ~18 KB per tile (Kartverket)
  SATELLITE: 25_000,   // ~25 KB per tile (ArcGIS)
};

/** Shared terrain-type metadata used by Map legend, RouteDetailView badges, and RouteLayer colors */
export const TERRAIN_META: Record<string, { emoji: string; label: string; color: string }> = {
  paved:    { emoji: '🛣️', label: 'Asfalt', color: '#6366f1' },
  gravel:   { emoji: '🪨', label: 'Grus',   color: '#d97706' },
  forest:   { emoji: '🌲', label: 'Skog',   color: '#16a34a' },
  sand:     { emoji: '🏖️', label: 'Sand',   color: '#eab308' },
  snow:     { emoji: '❄️', label: 'Snø',    color: '#94a3b8' },
  mountain: { emoji: '⛰️', label: 'Fjell',  color: '#8b5cf6' },
  coastal:  { emoji: '🌊', label: 'Kyst',   color: '#0ea5e9' },
};

export const VARSOM_API = {
  AVALANCHE: 'https://api01.nve.no/hydrology/forecast/avalanche/v6.0.0/api/RegionSummary/Detail/Today/1',
  FLOOD: 'https://api01.nve.no/hydrology/forecast/flood/v1.0.0/api/RegionSummary/Detail/Today/1'
};

// --- Gamification / Besøkslogg ---

export const CLEANUP_BADGES: Badge[] = [
  { id: 'clean_hands',    labelNo: 'Ryddehjelp',     labelEn: 'Clean Hands',    emoji: '🧹', descriptionNo: 'Første opprydning loggført!',     threshold: 1,  color: 'bg-teal-500' },
  { id: 'cleanup_hero',   labelNo: 'Oppryddingshelt', labelEn: 'Cleanup Hero',   emoji: '♻️', descriptionNo: '5 oppryddinger loggført',          threshold: 5,  color: 'bg-green-500' },
  { id: 'nature_keeper',  labelNo: 'Naturvokter',     labelEn: 'Nature Keeper',  emoji: '🌿', descriptionNo: '15 oppryddinger loggført',         threshold: 15, color: 'bg-emerald-600' },
  { id: 'cleanup_legend', labelNo: 'Oppryddingslegende', labelEn: 'Cleanup Legend', emoji: '🏅', descriptionNo: '50 oppryddinger loggført',      threshold: 50, color: 'bg-lime-500' },
];

export const VISIT_BADGES: Badge[] = [
  { id: 'first_steps', labelNo: 'Første steg', labelEn: 'First Steps', emoji: '🥾', descriptionNo: 'Ditt første besøk!', threshold: 1, color: 'bg-stone-500' },
  { id: 'explorer', labelNo: 'Utforsker', labelEn: 'Explorer', emoji: '🧭', descriptionNo: '5 unike plasser besøkt', threshold: 5, color: 'bg-blue-500' },
  { id: 'nature_lover', labelNo: 'Naturelsker', labelEn: 'Nature Lover', emoji: '🌿', descriptionNo: '10 unike plasser besøkt', threshold: 10, color: 'bg-emerald-500' },
  { id: 'wilderness_scout', labelNo: 'Villmarksspeideren', labelEn: 'Wilderness Scout', emoji: '🏕️', descriptionNo: '25 unike plasser besøkt', threshold: 25, color: 'bg-amber-500' },
  { id: 'fricamp_veteran', labelNo: 'Fricamp-veteran', labelEn: 'Fricamp Veteran', emoji: '⭐', descriptionNo: '50 unike plasser besøkt', threshold: 50, color: 'bg-purple-500' },
  { id: 'century_club', labelNo: 'Hundre-klubben', labelEn: 'Century Club', emoji: '🏆', descriptionNo: '100 unike plasser besøkt', threshold: 100, color: 'bg-yellow-500' },
];

export const USER_TIERS = [
  { level: 1, name: 'Utforsker', color: '#2E8B57', threshold: 1 },
  { level: 2, name: 'Stifinner', color: '#1E90FF', threshold: 5 },
  { level: 3, name: 'Eventyrer', color: '#CD7F32', threshold: 15 },
  { level: 4, name: 'Veiviser', color: '#C0C0C0', threshold: 30 },
  { level: 5, name: 'Legende', color: '#FFD700', threshold: 50 },
] as const;

// Toppnivået (Legende). Brukes bl.a. til å gi gratis Android Auto / CarPlay
// som bidragsbelønning, jf. hasPremiumAccess.
export const LEGEND_TIER_LEVEL = 5;

// --- Tier-baserte privilegier ---

export type ModerationAction = 'category_suggestions' | 'route_suggestions' | 'spot_deletions';

export interface TierPrivilege {
  skipPhotoRequirement: boolean;
  unlockedFeatures: PremiumFeatureKey[];
  canApproveCategorySuggestions: boolean;
  canApproveRouteSuggestions: boolean;
  canApproveSpotDeletions: boolean;
  descriptionNo: string;
  descriptionEn: string;
}

export const TIER_PRIVILEGES: Record<number, TierPrivilege> = {
  1: {
    skipPhotoRequirement: false,
    unlockedFeatures: [],
    canApproveCategorySuggestions: false,
    canApproveRouteSuggestions: false,
    canApproveSpotDeletions: false,
    descriptionNo: 'Fargering rundt avataren din',
    descriptionEn: 'Colored ring around your avatar',
  },
  2: {
    skipPhotoRequirement: true,
    unlockedFeatures: [],
    canApproveCategorySuggestions: false,
    canApproveRouteSuggestions: false,
    canApproveSpotDeletions: false,
    descriptionNo: 'Legg til nye steder uten bilde',
    descriptionEn: 'Add new spots without photos',
  },
  3: {
    skipPhotoRequirement: true,
    unlockedFeatures: ['satellite_map', 'fishing_layer', 'fire_danger'],
    canApproveCategorySuggestions: true,
    canApproveRouteSuggestions: false,
    canApproveSpotDeletions: false,
    descriptionNo: 'Satellittkart + fiskemuligheter + brannfare + godkjenn stedsforslag',
    descriptionEn: 'Satellite map + fishing opportunities + fire danger + approve spot suggestions',
  },
  4: {
    skipPhotoRequirement: true,
    unlockedFeatures: ['satellite_map', 'hiking_trails', 'fishing_layer', 'fire_danger'],
    canApproveCategorySuggestions: true,
    canApproveRouteSuggestions: true,
    canApproveSpotDeletions: false,
    descriptionNo: 'Turstier + fiskemuligheter + brannfare + godkjenn ruteforslag',
    descriptionEn: 'Hiking trails + fishing opportunities + fire danger + approve route suggestions',
  },
  5: {
    skipPhotoRequirement: true,
    unlockedFeatures: ['satellite_map', 'hiking_trails', 'gps_tracking', 'fishing_layer', 'fire_danger'],
    canApproveCategorySuggestions: true,
    canApproveRouteSuggestions: true,
    canApproveSpotDeletions: true,
    descriptionNo: 'GPS-sporing + full moderering',
    descriptionEn: 'GPS tracking + full moderation access',
  },
};

// --- Route View Gamification Badges ---
export const ROUTE_VIEW_BADGES = [
  { threshold: 100, badgeName: 'Stifinner', emoji: '🥉', color: '#CD7F32' },
  { threshold: 500, badgeName: 'Kjentmann', emoji: '🥈', color: '#C0C0C0' },
  { threshold: 1000, badgeName: 'Lokalguide', emoji: '🥇', color: '#FFD700' },
] as const;

// --- Prescanned county bounds (spots already generated by prescan scripts) ---
// Map center within these bounds → hide "Search for spots here" button
export const PRESCANNED_COUNTY_BOUNDS = [
  { name: 'Agder',      latMin: 57.9, latMax: 59.5, lonMin: 6.4,  lonMax: 9.7 },
  { name: 'Vestland',   latMin: 59.5, latMax: 62.2, lonMin: 4.5,  lonMax: 8.0 },
  { name: 'Akershus',   latMin: 59.5, latMax: 60.5, lonMin: 10.3, lonMax: 12.0 },
  { name: 'Trøndelag',  latMin: 62.4, latMax: 65.0, lonMin: 8.0,  lonMax: 14.5 },
  { name: 'Telemark',   latMin: 58.8, latMax: 60.2, lonMin: 7.5,  lonMax: 10.0 },
  { name: 'Rogaland',   latMin: 58.4, latMax: 59.8, lonMin: 5.3,  lonMax: 7.2 },
] as const;

export function isInPrescannedCounty(lat: number, lon: number): boolean {
  return PRESCANNED_COUNTY_BOUNDS.some(
    b => lat >= b.latMin && lat <= b.latMax && lon >= b.lonMin && lon <= b.lonMax
  );
}

// --- County-Based Notifications (norske fylker) ---
export const NORWEGIAN_COUNTIES = [
  'Agder',
  'Akershus',
  'Buskerud',
  'Finnmark',
  'Innlandet',
  'Møre og Romsdal',
  'Nordland',
  'Oslo',
  'Rogaland',
  'Telemark',
  'Troms',
  'Trøndelag',
  'Vestfold',
  'Vestland',
  'Østfold',
] as const;

// All Norwegian counties with approximate bounds for offline downloads
export const COUNTY_DOWNLOAD_BOUNDS: Record<string, { latMin: number; latMax: number; lonMin: number; lonMax: number }> = {
  'Agder':           { latMin: 57.9, latMax: 59.5, lonMin: 6.4,  lonMax: 9.7 },
  'Akershus':        { latMin: 59.5, latMax: 60.5, lonMin: 10.3, lonMax: 12.0 },
  'Buskerud':        { latMin: 59.4, latMax: 60.8, lonMin: 7.5,  lonMax: 10.3 },
  'Finnmark':        { latMin: 68.4, latMax: 71.2, lonMin: 22.0, lonMax: 31.1 },
  'Innlandet':       { latMin: 60.1, latMax: 62.6, lonMin: 8.5,  lonMax: 12.5 },
  'Møre og Romsdal': { latMin: 61.8, latMax: 63.2, lonMin: 5.2,  lonMax: 9.2 },
  'Nordland':        { latMin: 64.8, latMax: 69.4, lonMin: 11.0, lonMax: 17.5 },
  'Oslo':            { latMin: 59.8, latMax: 60.0, lonMin: 10.5, lonMax: 10.9 },
  'Rogaland':        { latMin: 58.4, latMax: 59.8, lonMin: 5.3,  lonMax: 7.2 },
  'Telemark':        { latMin: 58.8, latMax: 60.2, lonMin: 7.5,  lonMax: 10.0 },
  'Troms':           { latMin: 68.3, latMax: 70.3, lonMin: 15.5, lonMax: 22.0 },
  'Trøndelag':       { latMin: 62.4, latMax: 65.0, lonMin: 8.0,  lonMax: 14.5 },
  'Vestfold':        { latMin: 58.9, latMax: 59.6, lonMin: 9.4,  lonMax: 10.6 },
  'Vestland':        { latMin: 59.5, latMax: 62.2, lonMin: 4.5,  lonMax: 8.0 },
  'Østfold':         { latMin: 58.9, latMax: 59.8, lonMin: 10.5, lonMax: 12.1 },
};
